import * as React from 'react'
import cx from 'classnames'
import styles from './SplitViewSidebarListItem.sass'
import { isChildrenEmpty } from '../../utils/react-util'
import Link from '../Link'
import requestAnimationFrame from 'raf'
import Icon from 'components/Icon'

interface SplitViewSidebarListItemProps {
  children?: React.ReactNode
  active?: boolean
  visual?: React.ReactNode
  extras?: React.ReactNode
  disabled?: boolean
  onClick?: React.MouseEventHandler
  // Link-related props
  to?: string
  href?: string
  replace?: boolean
  ['data-test']?: string
  ['aria-current']?: React.AriaAttributes['aria-current']
  spacing?: 'none' | 'compact' | 'default'
}

/**
 " Split View Sidebar List Item
 */
export function SplitViewSidebarListItem({
  children,
  active,
  visual,
  extras,
  to,
  href,
  replace,
  onClick,
  disabled,
  spacing = 'default',
  ...rest
}: SplitViewSidebarListItemProps) {
  const clickable = !disabled && Boolean(to || href || onClick)
  const rootRef = React.useRef<HTMLAnchorElement | null>(null)
  // Scrolls to the active element when it becomes active.
  React.useEffect(() => {
    if (active) {
      requestAnimationFrame(() =>
        rootRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      )
    }
  }, [active])
  return (
    <Link
      ref={rootRef}
      to={to}
      replace={replace}
      href={href}
      onClick={onClick}
      className={cx(
        styles.root,
        active && styles.active,
        clickable && styles.clickable,
        disabled && styles.disabled,
        spacing && styles[`spacing-${spacing}`]
      )}
      {...rest}
    >
      {!isChildrenEmpty(visual) && (
        <div className={styles.visual}>{visual}</div>
      )}
      <div className={styles.main}>{children}</div>
      {!isChildrenEmpty(extras) && (
        <div className={styles.extras}>{extras}</div>
      )}
    </Link>
  )
}

interface SplitViewSidebarListItemPretitleProps {
  children?: React.ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
}

/**
 " Split View Sidebar List Item Pretitle
 */
export function SplitViewSidebarListItemPretitle({
  children,
  title,
  wrap,
}: SplitViewSidebarListItemPretitleProps) {
  return (
    <div title={title} className={cx(styles.pretitle, !wrap && styles.cutoff)}>
      {children}
    </div>
  )
}

interface SplitViewSidebarListItemTitleProps {
  children?: React.ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
}

/**
 " Split View Sidebar List Item Title
 */
export function SplitViewSidebarListItemTitle({
  children,
  title,
  wrap,
}: SplitViewSidebarListItemTitleProps) {
  return (
    <div title={title} className={cx(styles.title, !wrap && styles.cutoff)}>
      {children}
    </div>
  )
}

interface SplitViewSidebarListItemSubtitleProps {
  children?: React.ReactNode
  title?: string
  /**
   * If `true` disables the text cutoff behavior.
   */
  wrap?: boolean
  ['data-test']?: string
}

/**
 " Split View Sidebar List Item Subtitle
 */
export function SplitViewSidebarListItemSubtitle({
  children,
  title,
  wrap,
  'data-test': dataTest,
}: SplitViewSidebarListItemSubtitleProps) {
  return (
    <div
      title={title}
      className={cx(styles.subtitle, !wrap && styles.cutoff)}
      data-test={dataTest}
    >
      {children}
    </div>
  )
}

interface SplitViewSidebarListItemArrowProps {}

/**
 " Split View Sidebar List Item Arrow
 */
export function SplitViewSidebarListItemArrow({}: SplitViewSidebarListItemArrowProps) {
  return (
    <span className={styles.arrow}>
      <Icon name="arrow" />
    </span>
  )
}
